<script lang="ts">
import { defineComponent, PropType } from 'vue';
import CButtonIcon from '@/components/shared/CButtonIcon.vue';
import { faMapMarker, faMapMarkerAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import { ButtonIconType } from '@/enums/ButtonIconType.enum';
import { MapPopupData } from '@/models/MapPopupData.model';

enum ComponentEvent {
	CLOSE = 'close',
}

/**
 *
 * @version 1.0.0
 * @since
 */
export default defineComponent({
	name: 'MapPopup',
	components: { CButtonIcon },
	props: {
		data: {
			required: true,
			type: Object as PropType<MapPopupData>,
			default: () => ({}),
		},
	},
	setup() {
		return {
			faTimes,
			faMapMarker,
			faMapMarkerAlt,
			ButtonIconType,
		};
	},
	methods: {
		close() {
			this.$emit(ComponentEvent.CLOSE);
		},
	},
});
</script>

<template>
	<div class="map-popup flex-center flex-column">
		<div class="map-popup__details">
			<div class="map-popup__header">
				<div class="map-popup__subheader flex-space mb-4">
					<h4 class="map-popup__title m-0 mr-12">
						{{ data.title }}
					</h4>

					<c-button-icon
						class="map-popup__icon-close"
						:icon-name="faTimes.iconName"
						:type="ButtonIconType.FLAT"
						@click="close"
					/>
				</div>
				<small class="map-popup__subtitle">
					{{ data.subtitle }}
				</small>
				<hr class="map-popup__hr my-10">
				<div class="map-popup__body">
					<div class="flex-row mb-8">
						<span class="mr-12">Lat: {{ data.lat }}°</span>
						<span>Lon: {{ data.lon }}°</span>
					</div>
					<span>{{ data.info }}</span>
				</div>
			</div>
			<span class="map-popup__bottom-arrow" />
		</div>

		<font-awesome-layers class="map-popup__icon-marker-layers mt-8">
			<font-awesome-icon
				class="map-popup__icon-marker-background"
				:icon="faMapMarker.iconName"
			/>
			<font-awesome-icon
				class="map-popup__icon-marker"
				:icon="faMapMarkerAlt.iconName"
				transform="shrink-2"
			/>
		</font-awesome-layers>
	</div>
</template>

<style lang="scss">
.ol-overlay-container {
	margin-top: calc((#{tokens.$icon-marker-size} + #{tokens.$icon-marker-margin} + #{tokens.$icon-marker-margin}) * -1);
}
</style>

<style scoped lang="scss">
$popup-background-color: tokens.$color-white;

.map-popup {
	position: relative;
}

.map-popup__details {
	padding: 12px;
	background-color: $popup-background-color;
	box-shadow: 0 1px 6px rgba(0, 0, 0, 0.15);
	border-radius: 4px;
}

.map-popup__hr {
	border: 1px solid tokens.$color-gray-f0;
}

.map-popup__icon-close {
	@include tools.size(16px);
}

.map-popup__subtitle {
	@include tools.font-size(12px, 14px);

	color: tokens.$color-gray-89;
}

.map-popup__body {
	font-weight: 400;
}

.map-popup__bottom-arrow {
	@include tools.size(calc(#{tokens.$bottom-arrow-size} * 2));

	position: absolute;
	bottom: calc(-#{tokens.$bottom-arrow-size});
	left: calc(50% - #{tokens.$bottom-arrow-size});
	display: inline-block;
	background-color: $popup-background-color;
	transform: rotate(45deg);
	box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.15);
}

.map-popup__icon-marker-layers {
	position: absolute;
	bottom: calc((#{tokens.$icon-marker-size} + #{tokens.$icon-marker-margin} + #{tokens.$bottom-arrow-size}) * -1);
	font-size: tokens.$icon-marker-size;
	pointer-events: none;
}

.map-popup__icon-marker {
	color: $popup-background-color;
}

.map-popup__icon-marker-background {
	color: tokens.$color-blue;
}
</style>
